/**
 * Company Store using Zustand
 * A lista de empresas que abastece o campo "empresa" dos formulários.
 *
 * Qual empresa está ativa não mora aqui — é o contexto do manifesto (`manifestoStore`), que o
 * cabeçalho troca. Este armazenamento só serve às telas em que a empresa continua sendo
 * escolha: clínica, onde ela é filtro, e plataforma, que não tem recorte.
 *
 * A lista carrega junto o contexto de onde veio. Ela vive na memória da aba e trocar de
 * contexto não recarrega a página: sem esse carimbo, quem entrava numa clínica que atende
 * doze empresas e trocava para uma que atende uma continuava vendo as doze — nomes de
 * empresas de outro cliente, num contexto que não alcança nenhuma delas.
 */

import { useCallback } from 'react';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { Company } from '../types/company';
import { companyService } from '../services/companyService';
import { ChaveDoContexto, useChaveDoContexto } from './chaveDoContexto';

interface CompanyState {
  /** List of available companies */
  companies: Company[];
  /**
   * O contexto de onde a lista veio; fora dele ela não vale.
   *
   * `undefined` é "nunca se buscou", e é diferente de `null`, que é "buscou-se antes de haver
   * contexto" — sem essa distinção a lista nascia dada por carregada e a busca não saía.
   */
  contexto: ChaveDoContexto | undefined;
  /** Loading state */
  isLoading: boolean;
  /** Error message */
  error: string | null;
}

interface CompanyActions {
  /** Set the companies list */
  setCompanies: (companies: Company[], contexto: ChaveDoContexto) => void;
  /** Load companies from API */
  loadCompanies: (contexto: ChaveDoContexto) => Promise<void>;
  /** Set loading state */
  setLoading: (loading: boolean) => void;
  /** Set error */
  setError: (error: string | null) => void;
  /** Devolve o armazenamento ao estado inicial — sessão nova não herda a lista da anterior. */
  limpar: () => void;
}

type CompanyStore = CompanyState & CompanyActions;

const initialState: CompanyState = {
  companies: [],
  contexto: undefined,
  isLoading: false,
  error: null,
};

export const useCompanyStore = create<CompanyStore>()(
  devtools(
    (set) => ({
      ...initialState,

      setCompanies: (companies: Company[], contexto: ChaveDoContexto) => {
        set({ companies, contexto });
      },

      loadCompanies: async (contexto: ChaveDoContexto) => {
        try {
          set({ isLoading: true, error: null });

          const response = await companyService.getCompanies({ limit: 1000 });

          set({
            companies: response.data ?? [],
            contexto,
            isLoading: false,
            error: null,
          });
        } catch (error: unknown) {
          // O contexto entra também aqui: a pergunta foi feita, e a resposta é a falha. Sem
          // isso, quem busca enquanto não tem resposta reperguntaria a cada render, sem fim.
          set({
            contexto,
            isLoading: false,
            error: error instanceof Error ? error.message : 'Falha ao carregar empresas',
          });
        }
      },

      setLoading: (isLoading: boolean) => {
        set({ isLoading });
      },

      setError: (error: string | null) => {
        set({ error });
      },

      limpar: () => set({ ...initialState }),
    }),
    {
      name: 'company-store',
    }
  )
);

/** Referência estável: array novo a cada render reexecuta o efeito de quem consome. */
const SEM_EMPRESAS: Company[] = [];

/**
 * A lista como as telas consomem: vazia enquanto ela não for do contexto de agora.
 *
 * `carregada` responde "já perguntei neste contexto?", e é por ela que quem consome decide
 * buscar — nunca por a lista estar vazia. Uma clínica pode não atender empresa nenhuma, e a
 * resposta vazia é resposta: quem repergunta ao vê-la repergunta sem fim, porque a próxima
 * resposta é igual. A troca de contexto continua se resolvendo sozinha, porque é ela que faz
 * `carregada` voltar a ser falso.
 */
export const useCompanyList = () => {
  const contexto = useChaveDoContexto();
  const companies = useCompanyStore((estado) => estado.companies);
  const contextoDaLista = useCompanyStore((estado) => estado.contexto);
  const isLoading = useCompanyStore((estado) => estado.isLoading);
  const error = useCompanyStore((estado) => estado.error);
  const carregar = useCompanyStore((estado) => estado.loadCompanies);

  const carregada = contextoDaLista === contexto;

  return {
    companies: carregada ? companies : SEM_EMPRESAS,
    carregada,
    isLoading,
    error,
    loadCompanies: useCallback(() => carregar(contexto), [carregar, contexto]),
  };
};

export default useCompanyStore;
